import { Box, Theme } from '@mui/material';
import { ReactNode } from 'react';

import { BankIcon } from '@/components/icons/BankIcon';
import { CalendarCheck01Icon } from '@/components/icons/CalendarCheck01Icon';
import { CheckCircleIcon } from '@/components/icons/CheckCircleIcon';
import { Flag02Icon } from '@/components/icons/Flag02Icon';
import { Lightbulb02Icon } from '@/components/icons/Lightbulb02Icon';
import { ShieldDollarIcon } from '@/components/icons/ShieldDollarIcon';
import { COLORS } from '@/styles/tokens/colors';

export const ICON_TYPE_OPTIONS = [
  { display: 'Checkmark', value: 'check' },
  { display: 'Number', value: 'number' },
  { display: 'Flag', value: 'flag' },
  { display: 'Calendar', value: 'calendar' },
  { display: 'Shield', value: 'shield' },
  { display: 'Institution', value: 'institution' },
  { display: 'Lightbulb', value: 'lightbulb' },
] as const;

/**
 * Icon color token options for the presentation list item.
 * These tokens map to data visualization colors from the theme.
 */
export const ICON_COLOR_TOKEN_OPTIONS = [
  { display: 'Brand', value: 'PRIMARY' },
  { display: 'Negative', value: 'NEGATIVE' },
] as const;

export type IconColorToken = (typeof ICON_COLOR_TOKEN_OPTIONS)[number]['value'];

/**
 * Maps an icon color token to the actual color value from the theme.
 * Falls back to primary color if the token is invalid or undefined.
 */
export function getIconColorFromToken(
  theme: Theme,
  token?: IconColorToken | string
): string {
  switch (token) {
    case 'PRIMARY':
      return theme.palette.primary.main;
    case 'NEGATIVE':
      return theme.palette.error.main;
    default:
      return theme.palette.primary.main;
  }
}

/**
 * Gets the default icon color token for a given icon type.
 * Some icons default to negative (flag, institution) while others default to primary.
 */
export function getDefaultIconColorToken(
  iconType: (typeof ICON_TYPE_OPTIONS)[number]['value'] | undefined
): IconColorToken {
  switch (iconType) {
    case 'flag':
    case 'institution':
      return 'NEGATIVE';
    default:
      return 'PRIMARY';
  }
}

export function renderIconFromType({
  iconType,
  number,
  theme,
  iconColor,
}: {
  iconType: (typeof ICON_TYPE_OPTIONS)[number]['value'] | undefined;
  number?: number;
  theme: Theme;
  iconColor?: IconColorToken | string;
}): ReactNode {
  // Use the provided iconColor if specified, otherwise fall back to icon-type-specific default
  const resolvedColor = getIconColorFromToken(
    theme,
    iconColor ?? getDefaultIconColorToken(iconType)
  );

  switch (iconType) {
    case 'check':
      return <CheckCircleIcon size={16} color={resolvedColor} />;
    case 'number':
      return (
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: resolvedColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLORS.PRIMITIVES.WHITE,
            fontSize: '0.6rem',
            fontWeight: 600,
          }}
        >
          {number ?? 1}
        </Box>
      );
    case 'flag':
      return <Flag02Icon size={16} color={resolvedColor} />;
    case 'calendar':
      return <CalendarCheck01Icon size={16} color={resolvedColor} />;
    case 'shield':
      return <ShieldDollarIcon size={16} color={resolvedColor} />;
    case 'institution':
      return <BankIcon size={16} color={resolvedColor} />;
    case 'lightbulb':
      return <Lightbulb02Icon size={16} color={resolvedColor} />;
    default:
      return null;
  }
}
